/**
 * useChatPreview — último mensaje del chat cliente↔EC, para el panel
 * "Conversación" del Sidebar (piel Estudio, nav de 5 zonas).
 *
 * El backend solo trackea si la AGENCIA leyó los mensajes del cliente
 * (`leido_agencia_at`) — no existe el campo inverso — así que "no leído"
 * del lado cliente se resuelve con un timestamp local en localStorage,
 * seteado por `markChatSeenNow()` cuando el cliente abre /chat.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "./api.js";

const SEEN_KEY = "chat_last_seen_at";

export function markChatSeenNow() {
  try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch {}
}

// Algunos mensajes guardan el email como nombre del autor ("mathias@thecrewagencia.com").
// Se muestra la parte local con mayúscula ("Mathias"), nunca el email crudo.
function nombreHumano(n) {
  const s = (n || "").trim();
  if (!s) return "Equipo";
  if (!s.includes("@")) return s;
  const local = s.split("@")[0].split(/[._-]/)[0];
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : "Equipo";
}

export function useChatPreview(clienteId) {
  const [preview, setPreview] = useState(null); // { nombre, texto, unread } | null

  const refresh = useCallback(async () => {
    if (!clienteId) { setPreview(null); return; }
    try {
      // limit 20 (antes 1): además del último mensaje, cuenta los no leídos
      // para la píldora de la barra inferior móvil.
      const r = await api.chatListar(clienteId, { limit: 20 });
      const msgs = r.mensajes || [];
      const last = msgs[msgs.length - 1];
      if (!last) { setPreview(null); return; }
      let seen = null;
      try { seen = localStorage.getItem(SEEN_KEY); } catch {}
      const unread = last.autor_tipo === "agencia"
        && (!seen || new Date(last.created_at) > new Date(seen));
      const unreadCount = msgs.filter(m =>
        m.autor_tipo === "agencia" && !m.deleted_at && (!seen || new Date(m.created_at) > new Date(seen))
      ).length;
      setPreview({
        nombre: nombreHumano(last.autor_nombre_snapshot),
        texto: last.deleted_at ? "" : (last.texto || (last.adjuntos?.length ? "Archivo adjunto" : "")),
        unread,
        unreadCount,
      });
    } catch { setPreview(null); }
  }, [clienteId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 60_000);
    return () => clearInterval(interval);
  }, [refresh]);

  return preview;
}
